import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../lib/authContext";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const req = (p) => axios.get(API + p, { withCredentials: true });

const STATUS_STYLE = {
  available: "bg-[#00D2FF]/15 text-[#00D2FF] border-[#00D2FF]/40",
  limited: "bg-[#C9A961]/15 text-[#C9A961] border-[#C9A961]/40",
  planned: "bg-slate-700/40 text-slate-300 border-slate-600",
  disabled: "bg-slate-800/60 text-slate-500 border-slate-700",
};

export default function ControlCenter() {
  const { user, loading, signout } = useAuth();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [members, setMembers] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/account/sign-in"); return; }
    Promise.all([
      req("/control-center/overview"), req("/control-center/projects"),
      req("/control-center/modules"), req("/control-center/workspace/members"),
      req("/control-center/integrations/status"), req("/control-center/audit"),
    ]).then(([o, p, m, mem, i, a]) => {
      setOverview(o.data); setProjects(p.data.projects || []);
      setModules(m.data.modules || []); setMembers(mem.data.members || []);
      setIntegrations(i.data.integrations || []); setAudit(a.data.events || []);
    }).catch(() => toast.error("Failed to load Control Center"));
  }, [loading, user, nav]);

  if (loading) return <div className="text-slate-400 py-20 text-center">Loading…</div>;
  if (!user) return null;

  return (
    <div className="space-y-6 py-6" data-testid="control-center-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-tech text-xs text-slate-400">CONTROL CENTER</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{user.workspace?.name}</h1>
          <div className="text-xs text-slate-500 font-mono">Signed in as {user.user?.email_display} · role: {overview?.role || "…"}</div>
        </div>
        <div className="flex gap-3">
          {user.is_tif_platform_admin && (
            <Link to="/admin/control-center" className="text-xs font-tech text-[#C9A961] border border-[#C9A961]/40 rounded px-3 py-1.5" data-testid="tif-console-link">TIF console →</Link>
          )}
          <button onClick={async () => { await signout(); nav("/"); }} className="text-xs font-tech text-slate-400 border border-slate-700 rounded px-3 py-1.5" data-testid="account-signout">Sign out</button>
        </div>
      </div>

      {overview && (
        <div className="grid md:grid-cols-4 gap-4">
          <div className="panel p-3" data-testid="cc-projects-count"><div className="metric-label text-[10px]">Projects</div><div className="text-xl font-mono text-white mt-1">{overview.projects_count}</div></div>
          <div className="panel p-3"><div className="metric-label text-[10px]">Pending approvals</div><div className="text-xl font-mono text-white mt-1">{overview.approvals_pending}</div></div>
          <div className="panel p-3"><div className="metric-label text-[10px]">Members</div><div className="text-xl font-mono text-white mt-1">{members.length}</div></div>
          <div className="panel p-3"><div className="metric-label text-[10px]">Integrations ready</div><div className="text-xl font-mono text-white mt-1">{integrations.filter(i => i.ready).length}/{integrations.length}</div></div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="panel-elev p-5" data-testid="cc-projects-tile">
          <h2 className="text-sm font-tech text-slate-400 mb-3">PROJECTS</h2>
          {projects.length === 0 ? <div className="text-xs text-slate-500">No projects yet.</div> : (
            <div className="space-y-2">
              {projects.map(p => (
                <div key={p.id} className="flex justify-between items-center border-b border-slate-800 py-2 text-sm">
                  <span className="text-white">{p.name || p.id}</span>
                  <span className="text-[10px] font-mono text-slate-500">{p.status || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel-elev p-5" data-testid="cc-members-tile">
          <h2 className="text-sm font-tech text-slate-400 mb-3">WORKSPACE MEMBERS</h2>
          <div className="space-y-2">
            {members.map(m => (
              <div key={m.id} className="flex justify-between items-center border-b border-slate-800 py-2 text-sm">
                <span className="text-white font-mono text-xs">{m.user_id === user.user?.id ? "You" : m.user_id.slice(0, 12) + "…"}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${m.status === "active" ? "bg-[#00D2FF]/10 text-[#00D2FF]" : "bg-slate-800 text-slate-500"}`}>
                  {m.role} · {m.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="panel-elev p-5" data-testid="cc-modules-tile">
          <h2 className="text-sm font-tech text-slate-400 mb-3">ENGINEERING MODULES</h2>
          <div className="space-y-2">
            {modules.map(m => (
              <div key={m.key} className="flex justify-between items-start border-b border-slate-800 py-2 gap-3">
                <div>
                  <div className="text-sm text-white">{m.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{m.description}</div>
                </div>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded border shrink-0 ${STATUS_STYLE[m.status] || STATUS_STYLE.planned}`}>{m.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="panel-elev p-5" data-testid="cc-integrations-tile">
          <h2 className="text-sm font-tech text-slate-400 mb-3">INTEGRATIONS</h2>
          {integrations.map(i => (
            <div key={i.name} className="flex justify-between items-center border-b border-slate-800 py-2 text-sm">
              <span className="text-white capitalize">{i.name.replace("_", " ")}</span>
              <span className={`text-[10px] font-mono ${i.ready ? "text-[#00D2FF]" : "text-[#C9A961]"}`}>{i.ready ? "ready" : "not configured"}</span>
            </div>
          ))}
        </div>

        <div className="panel-elev p-5 lg:col-span-2" data-testid="cc-audit-tile">
          <h2 className="text-sm font-tech text-slate-400 mb-3">AUDIT HISTORY</h2>
          {audit.length === 0 ? <div className="text-xs text-slate-500">No events yet.</div> : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {audit.map(a => (
                <div key={a.id} className="flex justify-between text-[11px] font-mono border-b border-slate-800/60 py-1">
                  <span className="text-slate-300">{a.action}</span>
                  <span className="text-slate-500">{new Date(a.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
